import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Box, Typography, useTheme } from '@mui/material'
import moment from 'moment'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import { useSmartInterval } from '../../../generic/hooks/useSmartInterval'
import { durationStringToMilliseconds } from '../../../generic/DateTimeFunctions'
import { useMonitoringData } from './hooks/useMonitoringData'
import TimelineRow from './components/TimelineRow'
import OfTimeline from './components/OfTimeline'
import TimeAxis from './components/TimeAxis'
import { useDateFormatter } from '../../../generic/hooks/useDateFormatter'

/**
 * Monitoring Widget Component
 * Displays real-time monitoring of machine status, operators, and fabrication orders.
 * @param {object} props
 * @param {object} props.device - The device to monitor.
 */
const MonitoringWidget = (props) => {
  const { device, data, timeData: timeDataProp } = props
  const timeData = timeDataProp || data
  const theme = useTheme()
  const { t_ } = useSmartTranslation()
  const [now, setNow] = useState(moment())
  const { formatDateTime } = useDateFormatter()
  const { data: monitoringData, ofSegments, loadInfo, updateInfo } = useMonitoringData(device, timeData)

  // Update 'now' every minute to refresh the moving window
  useEffect(() => {
    const timer = setInterval(() => setNow(moment()), 60000)
    return () => clearInterval(timer)
  }, [])

  // Poll for data updates
  useSmartInterval(loadInfo, 60000, updateInfo)

  const mode = timeData?.timeRangeMode || 'last'
  let windowEnd = now
  let windowStart = moment(now).subtract(24, 'hours')

  if (mode === 'range' && timeData?.startTime && timeData?.endTime) {
    windowStart = moment(timeData.startTime)
    windowEnd = moment(timeData.endTime)
  } else {
    let durationMs = 24 * 60 * 60 * 1000
    if (timeData?.time) {
      try {
        durationMs = durationStringToMilliseconds(timeData.time)
      } catch (e) {
        durationMs = 24 * 60 * 60 * 1000
      }
    }
    windowStart = moment(now).subtract(durationMs, 'milliseconds')
  }

  // Use real data or empty object if no real data yet
  const displayData = monitoringData || {}

  return (
    <Box sx={{
      p: 2,
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      overflow: 'hidden',
      color: 'text.primary'
    }}
    >
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{
          bgcolor: 'background.paper',
          px: 1.5,
          py: 0.5,
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}
        >
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main', boxShadow: '0 0 0 2px rgba(74, 222, 128, 0.2)' }} />
          <Typography variant='caption' sx={{ fontWeight: 500, color: 'text.secondary' }}>
            {formatDateTime(windowStart)} - {formatDateTime(windowEnd)}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ flexGrow: 1, overflowY: 'auto', pr: 1 }}>
        <TimelineRow
          label={t_('Machine Status')}
          items={displayData.machineStatus || []}
          windowStart={windowStart}
          windowEnd={windowEnd}
        />
        <TimelineRow
          label={t_('Operator')}
          items={displayData.operators || []}
          windowStart={windowStart}
          windowEnd={windowEnd}
        />
        <OfTimeline
          label={t_('Fabrication Order')}
          segments={
            ofSegments && ofSegments.length > 0
              ? ofSegments
              : (displayData.orders || []).map(o => ({
                  fabricationOrder: { id: o.label, code: o.label, name: o.label },
                  jobReference: { id: o.label, code: o.label, name: o.label },
                  start: o.start,
                  end: o.end
                }))
          }
          windowStart={windowStart}
          windowEnd={windowEnd}
        />
      </Box>

      <TimeAxis windowStart={windowStart} windowEnd={windowEnd} />
    </Box>
  )
}

MonitoringWidget.propTypes = {
  device: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }),
  data: PropTypes.shape({
    time: PropTypes.string,
    startTime: PropTypes.string,
    endTime: PropTypes.string
  }),
  timeData: PropTypes.shape({
    time: PropTypes.string,
    startTime: PropTypes.string,
    endTime: PropTypes.string
  })
}

export default MonitoringWidget
